import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { isReviewDoc } from '../../stores/slices/reviewsSlice';
import ReviewSetupDialog from './ReviewSetupDialog';
import type { StartFrom } from './ReviewSetupForm';
import { StatusChip } from '../../design/primitives';
import { deckSummary, reviewOwner, reviewStatus, reviewSubline } from './reviewPage';


/**
 * Reviews (#189): the index of living documents — each grounded on a
 * perspective, refreshed on a schedule, built from pinned analyses.
 */
export default function ReviewsPage() {
  const savedThreads = useVoyagerStore((s) => s.savedThreads);
  const perspectives = useVoyagerStore((s) => s.perspectivesList);
  // "+ New review" is one dialog (#317): name · grounding · where it starts from — then the page
  const [setupOpen, setSetupOpen] = useState(false);
  // the page's three ways in (#568) open the same one dialog on their own choice
  const [setupFrom, setSetupFrom] = useState<StartFrom>('template');
  const openSetup = (from: StartFrom) => { setSetupFrom(from); setSetupOpen(true); };
  const loadThread = useVoyagerStore((s) => s.loadThread);
  const renameThread = useVoyagerStore((s) => s.renameThread);
  const deleteThread = useVoyagerStore((s) => s.deleteThread);
  // the row's menu: Rename (a dialog), Delete (a confirmation — a review's refresh workflow and traces go with it)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuId, setMenuId] = useState<string | null>(null);
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const closeMenu = () => { setMenuAnchor(null); setMenuId(null); };
  const menuReview = savedThreads.find((t) => t.id === menuId) || null;
  const deleting = savedThreads.find((t) => t.id === deleteId) || null;
  const reviews = useMemo(
    () => savedThreads.filter(isReviewDoc).sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
    [savedThreads],
  );
  const nameOf = (id: string | null | undefined) => (id ? perspectives.find((p) => p.id === id)?.name || id : '');

  const menus = (
    <>
      <ReviewSetupDialog open={setupOpen} initialFrom={setupFrom} onClose={() => setSetupOpen(false)} />
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        <MenuItem data-testid="review-rename" onClick={() => { if (menuReview) { setRenameId(menuReview.id); setRenameValue(menuReview.title); } closeMenu(); }}>Rename</MenuItem>
        <MenuItem data-testid="review-delete" sx={{ color: 'error.main' }} onClick={() => { setDeleteId(menuId); closeMenu(); }}>Delete</MenuItem>
      </Menu>
      <Dialog open={!!renameId} onClose={() => setRenameId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Rename review</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth size="small" value={renameValue} onChange={(e) => setRenameValue(e.target.value.slice(0, 200))}
            onKeyDown={(e) => { if (e.key === 'Enter' && renameValue.trim() && renameId) { renameThread(renameId, renameValue.trim()); setRenameId(null); } }}
            inputProps={{ 'aria-label': 'Review title' }} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameId(null)}>Cancel</Button>
          <Button variant="contained" disabled={!renameValue.trim()} data-testid="review-rename-save" onClick={() => { if (renameId) renameThread(renameId, renameValue.trim()); setRenameId(null); }}>Rename</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete {deleting?.title || 'this review'}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            The document, its pinned analyses, its schedule and every trace behind it go with it. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" data-testid="review-delete-confirm" onClick={() => { if (deleteId) deleteThread(deleteId); setDeleteId(null); }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return (
    <Box data-testid="reviews-page" sx={{ flex: 1, overflow: 'auto', px: { xs: 2, md: 3.5 }, py: 3 }}>
      <Box sx={{ maxWidth: 980, mx: 'auto', display: 'grid', gap: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography component="h1" sx={{ fontFamily: 'var(--display)', fontSize: 28, fontWeight: 700, letterSpacing: '-0.015em', lineHeight: 1.15 }}>Reviews</Typography>
            <Typography sx={{ color: 'var(--ink-3)', fontSize: 13.5 }}>Weekly reviews built in Stroma, and decks and dashboards Stroma checks</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" size="small" onClick={() => openSetup('deck')} data-testid="import-deck">Import a deck</Button>
            <Button variant="outlined" size="small" onClick={() => openSetup('report')} data-testid="connect-dashboard">Connect a dashboard</Button>
            <Button variant="contained" size="small" onClick={() => openSetup('template')} data-testid="new-review">New review</Button>
          </Box>
        </Box>
        <Box sx={{ border: '1px solid', borderColor: 'var(--line)', borderRadius: 3, bgcolor: 'var(--surface)', overflow: 'hidden' }}>
          {reviews.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center', display: 'grid', gap: 1, justifyItems: 'center' }} data-testid="reviews-empty">
              <Typography sx={{ fontSize: 16, fontWeight: 700 }}>No reviews yet — start one from a template</Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary', maxWidth: 520, lineHeight: 1.6 }}>
                Import last period's deck and every number it printed is re-derived from the warehouse before you trust it — or start from the Weekly Business Review template.
              </Typography>
              <Button variant="contained" size="small" onClick={() => openSetup('template')} data-testid="new-review-empty">Start a review</Button>
            </Box>
          )}
          {reviews.map((t) => {
            const status = reviewStatus(t);
            const owner = reviewOwner(t);
            const summary = deckSummary(t);
            return (
              <Box key={t.id} data-testid="review-row" sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 1.5, alignItems: 'center', px: 2, py: 1.75, borderTop: '1px solid', borderColor: 'var(--line-soft)', '&:first-of-type': { borderTop: 0 }, '&:hover': { bgcolor: 'var(--sunk)' } }}>
                <Box sx={{ minWidth: 0, cursor: 'pointer' }} role="button" tabIndex={0} onClick={() => loadThread(t.id)} onKeyDown={(e) => { if (e.key === 'Enter') loadThread(t.id); }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700 }} noWrap>{t.title}</Typography>
                    <StatusChip status={status.tone} label={status.label} size="small" data-testid="review-status" />
                    {t.closedAt && <Typography variant="caption" sx={{ color: 'text.disabled' }}>closed</Typography>}
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', color: 'var(--ink-3)', mt: 0.25 }} data-testid="review-subline">{reviewSubline(t, nameOf)}</Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: 'var(--ink-3)', mt: 0.5 }} data-testid="review-check-line">
                    {owner ? <b>{owner}</b> : null}{owner && summary ? ' · ' : ''}{summary || (owner ? '' : 'Not checked against a deck')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Button size="small" variant="outlined" onClick={() => loadThread(t.id)} data-testid="review-open">Open</Button>
                  <IconButton size="small" aria-label={`Actions for ${t.title}`} data-testid="review-actions" onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); setMenuId(t.id); }}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
      {menus}
    </Box>
  );
}
